// Разбор задачи: шаги решения под вердиктом.
//
// Один компонент на все четыре места, где разбор показывается — задача,
// пересмотр закрытой, диагностика, зачёт, — потому что разметка и правило
// показа у них общие и четыре копии разъехались бы молча.
//
// **Разбор открыт всегда, кнопки «показать» нет** (14 сентября 2026). В тот
// же день он впервые стал приходить и на верный ответ: сошедшийся ответ и
// верный метод — разные вещи, угадал и сосчитал вдвое длиннее журнал пишет
// одинаково, и способ сверить метод был один — ошибиться. Кнопка, за которой
// он сначала лежал на верном ответе, в тот же вечер убрана: лишнее нажатие
// между ответом и объяснением — это ровно та цена, из-за которой объяснение
// не читают.
//
// Пусто бывает при `unparsable`: опечатка не открывает ни эталона, ни
// разбора, и `steps` приходит пустым — тогда компонент не рисует ничего.
//
// **«Не совсем понятно» и «решал иначе»** (29 сентября 2026,
// docs/24-unclear.md) — две тихие иконки в правом нижнем углу разбора и
// только там, где передан `unclear`: у ученика на задаче и в истории.
// Взрослый смотрит те же разборы без них.
//
// **«Подробнее» у шага** (1 октября 2026, docs/27-more.md) — свёрнутое
// промежуточное звено под шагом, если `more` его несёт. Пишет разработчик по
// просьбе взрослого; просьбу взрослый оставляет иконкой у шага — её рисует
// `stepTool`, который передаёт только экран взрослого.

import { useState, type ReactNode } from "react";
import { api, useMutation, useQuery } from "../api";
import { Formula } from "../lib/Formula";
import { requestFeedback } from "../lib/feedback";
import { IconFork } from "./Icons";
import { ToolButton, UnclearButton, UnclearPanel } from "./Unclear";

export type SolutionUnclear = {
  learnerKey: string;
  itemKey: string;
  /** Экран словами, как в шапке: «Задача 3 из 16». Уходит в замечание. */
  where: string;
};

export type StepMore = { step: number; text: string };

export function Solution({
  steps,
  unclear,
  more,
  stepTool,
}: {
  steps: string[];
  unclear?: SolutionUnclear;
  more?: StepMore[];
  /** Своё у каждого шага — иконка и то, что под ней раскрывается. Номер шага с нуля. */
  stepTool?: (step: number) => ReactNode;
}) {
  if (steps.length === 0) return null;
  return (
    <>
      <ol className="solution">
        {steps.map((step, i) => {
          const extra = more?.find((m) => m.step === i);
          return (
            <li key={i} className={stepTool ? "solution__step--tool" : undefined}>
              <Formula source={step} />
              {extra !== undefined ? (
                <details className="solution__more">
                  <summary>Подробнее</summary>
                  <Formula source={extra.text} />
                </details>
              ) : null}
              {stepTool?.(i)}
            </li>
          );
        })}
      </ol>
      {unclear !== undefined ? <SolutionTools count={steps.length} at={unclear} /> : null}
    </>
  );
}

function SolutionTools({ count, at }: { count: number; at: SolutionUnclear }) {
  const { learnerKey, itemKey, where } = at;
  const marks = useQuery(api.unclear.forItem, { learnerKey, itemKey });
  const setSteps = useMutation(api.unclear.setSteps);
  const [open, setOpen] = useState(false);
  // Окошко закрывается и при переходе к другой задаче: компонент тот же.
  const [openFor, setOpenFor] = useState(itemKey);
  if (openFor !== itemKey) {
    setOpenFor(itemKey);
    setOpen(false);
  }

  return (
    <>
      <div className="solution__tools">
        {/* Другой способ — не жалоба на разбор, а его проверка с другой
            стороны: верный путь, которого в разборе нет; верный, но вдвое
            длиннее; верный ответ неверным путём. Журнал во всех трёх случаях
            пишет одно и то же, и узнать можно только так. */}
        <ToolButton
          title="Решал иначе — показать своё решение"
          onClick={() =>
            requestFeedback({
              place: { screen: "solve.method", where: `${where} · другое решение`, itemKey },
              hint: "Как ты решал: шаги словами или фото тетради.",
            })
          }
        >
          <IconFork size={18} />
        </ToolButton>
        <UnclearButton
          open={open}
          marked={(marks?.steps.length ?? 0) > 0}
          onClick={() => setOpen((o) => !o && marks !== undefined)}
        />
      </div>
      {open && marks !== undefined ? (
        <UnclearPanel
          prompt="Какие шаги непонятны"
          options={Array.from({ length: count }, (_, i) => ({ key: i, label: `${i + 1}` }))}
          saved={marks.steps}
          savedComment={marks.comment}
          onSave={(steps, comment) => setSteps({ learnerKey, itemKey, steps, comment })}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
